import { Blog } from '../apiClient/Blog';
import { Sale } from '../apiClient/Sale';
import { Users } from '../apiClient/Users';
import { Lessons } from '../apiClient/Lessons';
import { Faculties } from '../apiClient/Faculties';
import { Documents } from '../apiClient/Documents';
import { Pricing } from '../apiClient/Pricing';
import { SiteSettings } from '../apiClient/SiteSettings';
import { Orders } from '../apiClient/Orders';
import { Discount } from '../apiClient/Discount';
import { Dashboard } from '../apiClient/Dashboard';
import { OnlineLessons } from '../apiClient/OnlineLessons';
import { Practice } from '../apiClient/Practice';
import { AiTutor } from '../apiClient/AiTutor';
import { Community } from '../apiClient/Community';
import { Contact } from '../apiClient/Contact';
import { Admin } from '../apiClient/Admin';
import { AdminUsers } from '../apiClient/AdminUsers';
import { AdminContent } from '../apiClient/AdminContent';
import { AdminGroups } from '../apiClient/AdminGroups';
import { AdminChannels } from '../apiClient/AdminChannels';
import { AdminOnline } from '../apiClient/AdminOnline';
import { AdminFaculties } from '../apiClient/AdminFaculties';
import { AdminLibrary } from '../apiClient/AdminLibrary';
import { AdminCommerce } from '../apiClient/AdminCommerce';
import { AdminSettings } from '../apiClient/AdminSettings';
import { AdminBlog } from '../apiClient/AdminBlog';
import { AdminLogs } from '../apiClient/AdminLogs';
import { AdminStats } from '../apiClient/AdminStats';
import { RequestParams } from '../apiClient/http-client';
import { useAlert } from 'react-alert';
import { useNavigate } from 'react-router-dom';
import { beUrl } from '../Constants';

export class ApiHelper {
  alert = useAlert();
  navigate = useNavigate();

  worker = (x : any) => {
    let t = localStorage.getItem("token");
    if (t === null) {
      return;
    }
    let p : RequestParams = {headers: {"Authorization" : t}};
    return p;
  }

  url = beUrl

  params = {
      baseUrl: this.url,
      securityWorker: this.worker 
  };

  userApi = new Users(this.params);

  lessonsApi = new Lessons(this.params);
  
  saleApi = new Sale(this.params);

  blogApi = new Blog(this.params);

  facultiesApi = new Faculties(this.params);

  documentsApi = new Documents(this.params);

  pricingApi = new Pricing(this.params);

  siteSettingsApi = new SiteSettings(this.params);

  ordersApi = new Orders(this.params);

  discountApi = new Discount(this.params);

  dashboardApi = new Dashboard(this.params);

  onlineLessonsApi = new OnlineLessons(this.params);

  practiceApi = new Practice(this.params);

  aiTutorApi = new AiTutor(this.params);

  // The AI tutor answer is streamed as Server-Sent Events, which the generated JSON
  // client can't express — so this is the one place we read a response body directly.
  // Auth + error handling still go through ApiHelper (throwing the Response like the
  // generated client does, so callers can reuse processErrors / the 401 redirect).
  askTutorStream = async (
    data: { message: string; subject?: number; context?: string; image?: string },
    handlers: {
      onSources?: (sources: any[]) => void;
      onDelta?: (text: string) => void;
      onCap?: (message: string, remainingDaily: number, remainingWeekly: number) => void;
      onDone?: (remainingDaily: number, remainingWeekly: number) => void;
    },
    signal?: AbortSignal
  ): Promise<void> => {
    const token = localStorage.getItem("token");
    const res = await fetch(`${this.url}/AiTutor/AskStream`, {
      method: "POST",
      headers: { "Content-Type": "application/json", ...(token ? { Authorization: token } : {}) },
      body: JSON.stringify(data),
      signal,
    });

    if (!res.ok || !res.body) throw res;

    const reader = res.body.getReader();
    const decoder = new TextDecoder();
    let buffer = "";

    while (true) {
      const { value, done } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });

      let sep: number;
      while ((sep = buffer.indexOf("\n\n")) >= 0) {
        const line = buffer.slice(0, sep).split("\n").find((l) => l.startsWith("data:"));
        buffer = buffer.slice(sep + 2);
        if (!line) continue;

        const payload = line.slice(5).trim();
        if (!payload) continue;

        let ev: any;
        try { ev = JSON.parse(payload); } catch { continue; }

        switch (ev.type) {
          case "sources": handlers.onSources?.(ev.sources ?? []); break;
          case "delta": handlers.onDelta?.(ev.text ?? ""); break;
          case "cap": handlers.onCap?.(ev.message, ev.remainingDaily, ev.remainingWeekly); break;
          case "done": handlers.onDone?.(ev.remainingDaily, ev.remainingWeekly); break;
          case "error": throw new Error(ev.message || "Nepodařilo se získat odpověď od AI tutora");
        }
      }
    }
  }

  // Study materials are streamed through an auth-gated endpoint (not the public files
  // URL) so the originals can't be downloaded or shared by link. The generated JSON
  // client can't express a binary response, so — like askTutorStream — we read the body
  // directly while still sending the Bearer token and throwing the Response so callers
  // can reuse processErrors / the 401 redirect.
  fetchProtectedFile = async (path: string, signal?: AbortSignal): Promise<Blob> => {
    const token = localStorage.getItem("token");
    const res = await fetch(`${this.url}/Documents/File?path=${encodeURIComponent(path)}`, {
      headers: token ? { Authorization: token } : {},
      signal,
    });

    if (!res.ok) throw res;
    return await res.blob();
  }

  // Community chat images stream through an auth-gated endpoint (Community section) as a
  // blob — the public files URL serves the file-browser UI, not the image, so a plain
  // <img src> breaks after reload. Same shape as fetchProtectedFile.
  fetchCommunityImage = async (path: string, signal?: AbortSignal): Promise<Blob> => {
    const token = localStorage.getItem("token");
    const res = await fetch(`${this.url}/Community/Image?path=${encodeURIComponent(path)}`, {
      headers: token ? { Authorization: token } : {},
      signal,
    });

    if (!res.ok) throw res;
    return await res.blob();
  }

  // Invoices are streamed through an auth-gated endpoint (Bearer token required), so a plain
  // link can't carry the token — fetch the PDF with the header and trigger a browser download.
  downloadInvoice = (invoiceId: string, fileName?: string) =>
    this.saveBlob(`${this.url}/Documents/Invoice/${invoiceId}`, fileName ?? `faktura-${invoiceId}.pdf`);

  downloadAdminInvoice = (invoiceId: string, fileName?: string) =>
    this.saveBlob(`${this.url}/AdminCommerce/Invoice/${invoiceId}`, fileName ?? `faktura-${invoiceId}.pdf`);

  saveBlob = async (url: string, fileName: string): Promise<void> => {
    const token = localStorage.getItem("token");
    const res = await fetch(url, { headers: token ? { Authorization: token } : {} });
    if (!res.ok) throw res;

    const objectUrl = URL.createObjectURL(await res.blob());
    const a = document.createElement("a");
    a.href = objectUrl;
    a.download = fileName;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(objectUrl);
  }

  communityApi = new Community(this.params);

  contactApi = new Contact(this.params);

  // Admin APIs authenticate with the normal user JWT — the backend gates them on the
  // "Admin" role claim baked into that token at login. There is no separate admin secret.
  adminWorker = (x : any) => {
    let t = localStorage.getItem("token");
    if (t === null) return;
    let p : RequestParams = { headers: { "Authorization": t } };
    return p;
  }

  adminParams = {
      baseUrl: this.url,
      securityWorker: this.adminWorker
  };

  adminApi = new Admin(this.adminParams);

  adminUsersApi = new AdminUsers(this.adminParams);

  adminContentApi = new AdminContent(this.adminParams);

  adminGroupsApi = new AdminGroups(this.adminParams);

  adminChannelsApi = new AdminChannels(this.adminParams);

  adminOnlineApi = new AdminOnline(this.adminParams);

  adminFacultiesApi = new AdminFaculties(this.adminParams);

  adminLibraryApi = new AdminLibrary(this.adminParams);

  adminCommerceApi = new AdminCommerce(this.adminParams);

  adminStatsApi = new AdminStats(this.adminParams);

  adminSettingsApi = new AdminSettings(this.adminParams);

  adminBlogApi = new AdminBlog(this.adminParams);

  adminLogsApi = new AdminLogs(this.adminParams);

  // Admin 401 (missing/expired token) or 403 (token lacks the Admin role) -> tell the
  // admin gate to re-evaluate (it shows the sign-in prompt or the "not authorized" screen)
  // instead of surfacing a raw error.
  adminError = async(x : any) => {
    if (x && (x.status === 401 || x.status === 403)) {
      window.dispatchEvent(new Event("admin-unauthorized"));
      return;
    }
    await this.processErrors(x);
  }

  processErrors = async(x) => {
    if (x && x.status === 401) {
      localStorage.removeItem("token");
      this.navigate("/prihlaseni");
      return;
    }

    // Non-Response errors (network failure, or .json() on a non-JSON body such
    // as the SPA fallback HTML when the API endpoint is missing) have no .text().
    // A SyntaxError specifically means we parsed an empty/non-JSON body — don't
    // leak the raw "JSON.parse: unexpected end of data" text to the user.
    if (!x || typeof x.text !== "function") {
      const message = x instanceof SyntaxError ? null : (x && x.message);
      this.alert.error(message || "Chyba spojení se serverem nebo neočekávaná odpověď API.");
      return;
    }

    let text = await x.text();

    let body;
    try {
        body = JSON.parse(text);
    } catch {
        // Body isn't JSON (e.g. plain text or SPA fallback HTML) — show it as-is.
        this.alert.error(text);
        return;
    }

    // ASP.NET validation problem-details: { errors: { field: ["msg", ...] } }
    if (body && typeof body === "object" && !Array.isArray(body) && body.errors) {
        Object.keys(body.errors).forEach((i) => {
            body.errors[i].forEach((y : string) => this.alert.error(y));
        });
        return;
    }

    // Bare string ("Chyba objednávky") or array (["Email je obsazen"]) — show the
    // message itself, not its JSON encoding with surrounding quotes/brackets.
    if (Array.isArray(body)) {
        body.forEach((y) => this.alert.error(String(y)));
        return;
    }

    this.alert.error(typeof body === "string" ? body : (body?.title || body?.message || text));
  }
}
